import { useMemo, useEffect, useCallback, useRef } from "react"
import debounce from "lodash.debounce"
import Cookies from "js-cookie"
import { getSegmentDeviceId } from "utils/getSegmentDeviceId"

import { useUser } from "store/contexts/user"
import { useComponentizedPage } from "store/contexts/componentized-page"
import { getViewport } from "utils"

const DEBOUNCE_AMOUNT = 2000

// Add tracking state storage
const trackingState = new WeakMap()
const trackedElements = new Set()

const isBrowser = typeof window !== "undefined"

const FB_CAPI_EVENTS = [
  "Product Added",
  "Order Completed",
  "Webinar Registered",
  "Meeting Scheduled",
  "Email Registered",
]

const useTracking = () => {
  const viewport = getViewport()
  const {
    user: { loggedIn },
  } = useUser()
  const { pageId = null } = useComponentizedPage()
  let articleId
  let segmentDeviceId

  if (isBrowser) {
    articleId = document.querySelector("meta[name='mbg.article_id']")?.content
    segmentDeviceId = getSegmentDeviceId()
  }

  const baseProps = useMemo(
    () => ({
      viewport,
      logged_in: loggedIn,
      ...(pageId && { pageId }),
      ...(articleId && {
        article_id: articleId,
        page_type: "Article",
      }),
    }),
    [viewport, loggedIn, pageId, articleId],
  )

  const hasAnalytics = () => isBrowser && Boolean(window.analytics)
  const hasElement = (elementRef) => elementRef && elementRef.current

  /**
   * Base tracking function.
   */
  const track = useCallback(
    (event, props) => {
      if (!hasAnalytics()) {
        return
      }

      // Add fbp and fbc to FB CAPI events
      let eventProps = props
      const lowerCaseFBEvents = FB_CAPI_EVENTS.map((eventName) => eventName.toLowerCase())
      if (lowerCaseFBEvents.includes(event.toLowerCase())) {
        eventProps = {
          ...eventProps,
          fbp: Cookies.get("_fbp") || "",
          fbc: Cookies.get("_fbc") || "",
        }
      }

      window?.analytics.track(
        event,
        { ...baseProps, ...eventProps },
        { ...(segmentDeviceId && { device: { id: segmentDeviceId } }) },
      )
    },
    [baseProps, segmentDeviceId],
  )

  /**
   * Tracks when an element is rendered for the first time (tracks only once because of debounce).
   */
  const trackRender = useCallback(
    (elementRef, trackingId, extraProps, onTrack, renderedFlag = "rendered") => {
      if (!hasAnalytics()) {
        return
      }

      useEffect(() => {
        if (!hasElement(elementRef)) {
          return () => {}
        }

        const element = elementRef.current
        const state = trackingState.get(element) || {}
        const elementKey = `${element.id || element.className}-${trackingId}-${renderedFlag}`

        // Check if we've already tracked this element with this ID
        if (state[renderedFlag] || state.trackingId === trackingId || trackedElements.has(elementKey)) {
          return () => {}
        }

        const trackDebounced = debounce((trackProps) => {
          if (!hasElement(elementRef)) {
            return
          }

          const currentElement = elementRef.current
          const currentState = trackingState.get(currentElement) || {}
          const currentKey = `${currentElement.id || currentElement.className}-${trackingId}-${renderedFlag}`
          
          if (currentState[renderedFlag] || currentState.trackingId === trackingId || trackedElements.has(currentKey)) {
            return
          }

          // Update tracking state
          trackingState.set(currentElement, {
            ...currentState,
            trackingId,
            [renderedFlag]: true
          })
          trackedElements.add(currentKey)

          if (onTrack) {
            onTrack(trackProps)
          } else {
            track("Element Rendered", trackProps)
          }
        }, DEBOUNCE_AMOUNT)

        const props = { ...(trackingId && { element_id: trackingId }), ...extraProps }
        trackDebounced(props)

        return () => {
          trackDebounced.cancel()
        }
      }, [trackingId, extraProps, onTrack, renderedFlag])
    },
    [track]
  )

  /**
   * Tracks an element when 50% of it is visible (tracks only once because of debounce).
   */
  const trackView = useCallback(
    (elementRef, trackingId, extraProps, onTrack, viewedFlag = "viewed") => {
      if (!hasAnalytics()) {
        return
      }

      const trackDebounced = useMemo(
        () =>
          debounce((props) => {
            if (!hasElement(elementRef) || elementRef.current[viewedFlag]) {
              return
            }
            elementRef.current[viewedFlag] = true

            if (onTrack) {
              onTrack(props)
            } else {
              track("Element Viewed", props)
            }
          }, DEBOUNCE_AMOUNT),
        [track, onTrack, elementRef, viewedFlag]
      )

      useEffect(() => {
        if (!hasElement(elementRef)) {
          return () => {}
        }

        const props = {
          ...(trackingId && { element_id: trackingId }),
          ...extraProps,
        }

        const observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              trackDebounced(props)
            }
          },
          { threshold: [0.5] }
        )
        const element = elementRef.current
        observer.observe(element)

        return () => {
          observer.unobserve(element)
          trackDebounced.cancel()
        }
      }, [trackingId, extraProps, trackDebounced])
    },
    [track]
  )

  /**
   * Tracks when a user clicks an element.
   */
  const trackClick = useCallback(
    (elementRef, trackingId, extraProps, onTrack) => {
      if (!hasAnalytics()) {
        return
      }

      useEffect(() => {
        if (!hasElement(elementRef)) {
          return () => {}
        }

        const props = {
          ...(trackingId && { element_id: trackingId }),
          ...extraProps,
        }

        const handleClick = () => {
          if (onTrack) {
            onTrack(props)
          } else {
            track("Element Clicked", props)
          }
        }

        const element = elementRef.current
        element.addEventListener("click", handleClick)

        return () => element.removeEventListener("click", handleClick)
      }, [track, onTrack, trackingId, extraProps])
    },
    [track],
  )

  return { hasAnalytics, track, trackRender, trackView, trackClick }
}

export default useTracking
